import { assertPigmentCapabilityManifest, canonicalPigmentJson, reconcilePigmentDocument, sha256CanonicalPigmentJson, } from "@pigment/core";
import { assertPigmentBrowserInspectionReport, assertPigmentBrowserInspectionJob, } from "@pigment/browser-inspector";
export { PIGMENT_AUTHORING_CONTEXT_SCHEMA, createPigmentAuthoringContext, } from "./authoring-context.js";
export { PigmentAuthoringEditError, applyPigmentAuthoringGuidedEdit, instantiatePigmentAuthoringBlock, parsePigmentAuthoringPropertyValue, pigmentAuthoringPlacementOptions, pigmentAuthoringPropertyOptions, pigmentAuthoringValueSource, } from "./authoring-edit-model.js";
import { createPigmentAuthoringContextFromResolved, resolveAndVerifyPigmentAuthoringBase, } from "./authoring-context.js";
/** A host refusal is data: callers retain its status and complete typed body. */
export class PigmentRefusal extends Error {
    status;
    body;
    constructor(status, body) {
        super(body.message || body.detail || body.error || `Pigment request refused with HTTP ${status}.`);
        this.name = "PigmentRefusal";
        this.status = status;
        this.body = body;
        Object.setPrototypeOf(this, new.target.prototype);
    }
    toJSON() {
        return { status: this.status, body: this.body };
    }
}
export function isPigmentRevisionMismatch(error) {
    if (!(error instanceof PigmentRefusal) || !isRecord(error.body.conflict))
        return false;
    return error.body.conflict.code === "revision_mismatch"
        && Number.isSafeInteger(error.body.conflict.expectedRevision)
        && Number.isSafeInteger(error.body.conflict.actualRevision);
}
export const PIGMENT_INSPECTED_SAVE_STATE_SCHEMA = "pigment.inspected-save-state.v1";
export function createPigmentClient(options) {
    return new PigmentClient(options);
}
export function createPigmentInspectionExecutorClient(options) {
    return new PigmentInspectionExecutorClient(options);
}
export class PigmentClient {
    options;
    constructor(options) {
        if (!options.baseUrl.trim())
            throw new Error("Pigment requires a baseUrl.");
        const parsed = new URL(options.baseUrl);
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:")
            throw new Error("Pigment baseUrl must use HTTP or HTTPS.");
        if (parsed.username || parsed.password)
            throw new Error("Pigment baseUrl must not include credentials; use the token option.");
        if (typeof options.token === "string" && !options.token.trim())
            throw new Error("Pigment token must be non-empty when supplied.");
        // Requests are rooted at /api, so origin is the effective authority
        // endpoint. Keeping only the origin also prevents path/query material from
        // becoming accidental durable state in inspected-save checkpoints.
        this.options = { ...options, baseUrl: parsed.origin };
    }
    async discover() {
        const value = await this.request("api/site/capabilities");
        try {
            return assertPigmentCapabilityManifest(value);
        }
        catch (error) {
            throw protocolRefusal("invalid_capability_manifest", error instanceof Error ? error.message : String(error));
        }
    }
    read(page) {
        return this.request(`api/site/${pageSegment(page)}`);
    }
    /**
     * Read one host draft and project it with the exact public capability
     * manifest. This remains a GET-only convenience; it creates no preview,
     * receipt, or mutation authority.
     */
    async authoringContext(page) {
        return (await this.authoringSnapshot(page)).context;
    }
    /**
     * Read and verify the complete authoring base without crossing mutation
     * authority. Consumers should initialize desired-state editors from this
     * snapshot instead of guessing whether draft, published, or seed content is
     * canonical.
     */
    async authoringSnapshot(page) {
        const requestedPage = page.trim();
        const manifest = await this.discover();
        const content = await this.read(page);
        if (content?.type !== "ok.site-content.v2"
            || content.page !== requestedPage
            || !Number.isSafeInteger(content.draftRevision)
            || content.draftRevision < 0
            || !Object.hasOwn(content, "draft")) {
            throw protocolRefusal("invalid_authoring_content", "Pigment host returned content that does not bind the requested page and revision.", {
                requestedPage,
            });
        }
        try {
            const authoringBase = await resolveAndVerifyPigmentAuthoringBase(content);
            return {
                context: createPigmentAuthoringContextFromResolved(manifest, content, authoringBase),
                content,
                authoringBase: authoringBase,
            };
        }
        catch (error) {
            throw protocolRefusal("invalid_authoring_base", error instanceof Error ? error.message : "Pigment host returned a malformed authoring base.", { requestedPage });
        }
    }
    /**
     * Compile against an already displayed, verified snapshot without reading
     * the host again. Editors must use this form so a concurrent host change is
     * surfaced by the later revision-bound prepare instead of silently changing
     * the base beneath the user's desired document.
     */
    reconcileSnapshot(snapshot, desired) {
        const { context, content, authoringBase } = snapshot;
        return reconcilePigmentDocument({
            base: {
                revision: authoringBase?.revision ?? content.draftRevision,
                // The core boundary owns the typed invalid-base refusal when the host
                // exposes neither an explicit base nor a legacy persisted draft. The
                // client must never manufacture an empty document.
                document: (authoringBase?.document ?? null),
            },
            desired,
            manifest: context.manifest,
        });
    }
    /**
     * Convenience form for stateless agents: read one exact GET-only authoring
     * snapshot and compile against it. Stateful editors should retain the
     * displayed snapshot and call `reconcileSnapshot` instead.
     */
    async reconcile(page, desired) {
        return this.reconcileSnapshot(await this.authoringSnapshot(page), desired);
    }
    plan(page, mutation) {
        return this.request(`api/site/${pageSegment(page)}/plan`, { method: "POST", body: { mutation } });
    }
    commit(page, mutation, inspectionAcceptance) {
        assertPigmentInspectionAcceptanceReference(inspectionAcceptance);
        return this.request(`api/site/${pageSegment(page)}`, { method: "PATCH", body: { mutation, inspectionAcceptance } });
    }
    preview(page, mutation, options = {}) {
        return this.request(`api/site/${pageSegment(page)}/preview`, {
            method: "POST",
            body: { mutation, ...options },
        });
    }
    inspectPreview(previewId) {
        return this.request(`api/site/previews/${pathSegment(previewId, "previewId")}`);
    }
    async requestInspectionJob(prepared) {
        assertPrepared(prepared);
        const response = await this.request(`api/site/previews/${pathSegment(prepared.preview.artifact.id, "previewId")}/inspection-jobs`, {
            method: "POST",
            body: { schema: "pigment.inspection-job-request.v1" },
        });
        const issued = assertPigmentInspectionJobResponse(response);
        const binding = await createPigmentInspectionAuthorityBinding(prepared);
        if (!sameJson(issued.job.lineage, binding.preview)) {
            throw protocolRefusal("inspection_job_lineage_mismatch", "Host-issued inspection job does not bind the exact prepared Pigment preview.");
        }
        return issued.job;
    }
    async readInspectionAcceptance(prepared, job) {
        assertPrepared(prepared);
        const binding = await createPigmentInspectionAuthorityBinding(prepared);
        assertPigmentBrowserInspectionJob(job);
        if (!sameJson(job.lineage, binding.preview)) {
            throw protocolRefusal("inspection_lineage_mismatch", "Inspection job does not bind the exact prepared Pigment preview.");
        }
        const value = await this.request(`api/site/previews/${pathSegment(prepared.preview.artifact.id, "previewId")}/inspection-jobs/${pathSegment(job.jobId, "jobId")}/acceptance`);
        const status = await assertPigmentInspectionAcceptanceStatus(value, binding);
        if (status.jobId !== job.jobId)
            throw protocolRefusal("inspection_job_mismatch", "Acceptance status names a different inspection job.");
        return status;
    }
    /**
     * Read terminal browser evidence for one prepared mutation. This author route
     * never sends executor claim material. Accepted reports must bind the exact
     * receipt that unlocks commit; failed reports must bind the exact durable
     * failure returned for the same inspection job.
     */
    async readInspectionReport(prepared, job, terminal) {
        assertPrepared(prepared);
        const binding = await createPigmentInspectionAuthorityBinding(prepared);
        assertPigmentBrowserInspectionJob(job);
        if (!sameJson(job.lineage, binding.preview)) {
            throw protocolRefusal("inspection_lineage_mismatch", "Inspection job does not bind the exact prepared Pigment preview.");
        }
        if (isPigmentInspectionFailure(terminal)) {
            // The failure's report ID/hash are checked against the returned report.
            // Its job identity is the path/job argument, already bound above.
        }
        else {
            await verifyPigmentInspectionAcceptanceReceipt(terminal, binding, { allowExpired: true });
            if (terminal.body.inspection.jobId !== job.jobId) {
                throw protocolRefusal("inspection_acceptance_job_mismatch", "Inspection acceptance names a different browser inspection job.");
            }
        }
        const previewId = prepared.preview.artifact.id;
        const value = await this.request(`api/site/previews/${pathSegment(previewId, "previewId")}/inspection-jobs/${pathSegment(job.jobId, "jobId")}/report`);
        return assertPigmentInspectionReportRead(value, previewId, job, terminal);
    }
    /** Wait for a passed acceptance or a durable failed inspection without discarding failure evidence. */
    async awaitInspectionTerminal(prepared, job, options = {}) {
        const pollIntervalMs = Math.max(100, Math.min(10_000, options.pollIntervalMs ?? 1_000));
        const timeoutMs = Math.max(pollIntervalMs, Math.min(30 * 60_000, options.timeoutMs ?? 5 * 60_000));
        const deadline = Date.now() + timeoutMs;
        while (true) {
            if (options.signal?.aborted)
                throw protocolRefusal("inspection_wait_aborted", "Waiting for browser inspection was aborted.");
            const status = await this.readInspectionAcceptance(prepared, job);
            if (status.status !== "pending")
                return status;
            if (Date.now() >= deadline)
                throw protocolRefusal("inspection_wait_timeout", `Host inspection remained pending for ${timeoutMs}ms.`);
            await waitFor(pollIntervalMs, options.signal);
        }
    }
    async awaitInspectionAcceptance(prepared, job, options = {}) {
        const terminal = await this.awaitInspectionTerminal(prepared, job, options);
        if (terminal.status === "accepted")
            return terminal.acceptance;
        throw new PigmentRefusal(422, {
            type: "pigment.inspection-failed.v1",
            error: terminal.failure.code,
            message: terminal.failure.message,
            failure: terminal.failure,
        });
    }
    async publish(page, receipt) {
        assertPigmentPublishReceipt(receipt);
        const requestedPage = page.trim();
        const value = await this.request(`api/site/${pageSegment(page)}/publish`, { method: "POST", body: { receipt } });
        if (!isPigmentPublished(value) || value.page !== requestedPage) {
            throw protocolRefusal("invalid_published_document", "Pigment host returned a publication result that does not bind the requested page and immutable publication identity.", { requestedPage });
        }
        const documentSha256 = await sha256CanonicalPigmentJson(value.published);
        if (value.publication.documentSha256 !== documentSha256) {
            throw protocolRefusal("published_document_hash_mismatch", "Pigment host returned a publication identity that does not bind the published document bytes.", { expectedDocumentSha256: documentSha256, actualDocumentSha256: value.publication.documentSha256 });
        }
        const publishReceiptSha256 = await sha256CanonicalPigmentJson(receipt);
        if (value.publication.publishReceiptSha256 !== publishReceiptSha256) {
            throw protocolRefusal("published_receipt_hash_mismatch", "Pigment host returned a publication identity that does not bind the submitted publish receipt.", { expectedPublishReceiptSha256: publishReceiptSha256, actualPublishReceiptSha256: value.publication.publishReceiptSha256 });
        }
        return value;
    }
    /**
     * Compose discovery, authority read, write-free planning, and immutable
     * preview materialization. This does not mutate shared content.
     */
    async prepare(page, mutation, options = {}) {
        const manifest = await this.discover();
        const current = await this.read(page);
        const plan = await this.plan(page, mutation);
        const preview = await this.preview(page, mutation, options);
        const artifact = preview.artifact;
        const expectedMutationHash = await sha256CanonicalPigmentJson({ page, manifestVersion: manifest.version, mutation });
        if (current.draftRevision !== mutation.expectedRevision
            || plan.baseRevision !== mutation.expectedRevision
            || artifact.baseRevision !== plan.baseRevision
            || artifact.candidateRevision !== plan.candidateRevision
            || artifact.candidateHash !== plan.candidateHash
            || artifact.mutationHash !== expectedMutationHash
            || artifact.page !== page) {
            throw protocolRefusal("prepared_lineage_mismatch", "Pigment plan and preview do not describe one exact authority lineage.", {
                currentDraftRevision: current.draftRevision,
                mutationExpectedRevision: mutation.expectedRevision,
                planBaseRevision: plan.baseRevision,
                planCandidateRevision: plan.candidateRevision,
                previewBaseRevision: artifact.baseRevision,
                previewCandidateRevision: artifact.candidateRevision,
                planCandidateHash: plan.candidateHash,
                previewCandidateHash: artifact.candidateHash,
                expectedMutationHash,
                previewMutationHash: artifact.mutationHash,
            });
        }
        return {
            schema: "pigment.prepared-mutation.v1",
            page,
            mutation,
            manifestVersion: manifest.version,
            observedDraftRevision: current.draftRevision,
            plan,
            preview,
            preparedAt: new Date().toISOString(),
        };
    }
    async commitPrepared(prepared, acceptance) {
        assertPrepared(prepared);
        const binding = await createPigmentInspectionAuthorityBinding(prepared);
        // The host is the mutation authority and distinguishes a new expired
        // commit from an exact replay of a commit it already applied. The client
        // therefore verifies durable receipt integrity here without pre-empting a
        // legitimate lost-response replay solely on wall-clock expiry.
        await verifyPigmentInspectionAcceptanceReceipt(acceptance, binding, { allowExpired: true });
        const inspectionAcceptance = pigmentInspectionAcceptanceReference(acceptance);
        const commit = await this.commit(prepared.page, prepared.mutation, inspectionAcceptance);
        if (commit.previousRevision !== prepared.plan.baseRevision
            || commit.revision !== prepared.plan.candidateRevision) {
            throw protocolRefusal("commit_lineage_mismatch", "Committed revision differs from the inspected Pigment plan.", {
                plannedBaseRevision: prepared.plan.baseRevision,
                plannedCandidateRevision: prepared.plan.candidateRevision,
                commitPreviousRevision: commit.previousRevision,
                commitRevision: commit.revision,
            });
        }
        return {
            schema: "pigment.committed-mutation.v3",
            prepared,
            acceptance,
            commit,
            receipt: {
                schema: "pigment.publish-receipt.v3",
                expectedDraftRevision: commit.revision,
                expectedCandidateHash: prepared.plan.candidateHash,
                inspectionAcceptance,
            },
            committedAt: new Date().toISOString(),
        };
    }
    async publishPrepared(committed) {
        await verifyCommittedMutation(committed);
        return this.publish(committed.prepared.page, committed.receipt);
    }
    /**
     * Prepare, inspect, verify the exact browser report, and commit one semantic
     * mutation. Publication remains a separate explicit `publishPrepared` call.
     */
    async saveInspected(page, mutation, options = {}) {
        const prepared = await this.prepare(page, mutation, {
            ...(options.ttlSeconds === undefined ? {} : { ttlSeconds: options.ttlSeconds }),
        });
        const state = {
            schema: PIGMENT_INSPECTED_SAVE_STATE_SCHEMA,
            phase: "prepared",
            authorityBaseUrl: this.options.baseUrl,
            prepared,
        };
        await checkpointInspectedSave(state, options.checkpoint);
        return this.advanceInspectedSave(state, options);
    }
    /** Resume an exact checkpoint without repeating already completed phases. */
    async resumeInspectedSave(value, options = {}) {
        const state = await assertPigmentInspectedSaveState(value);
        if (state.authorityBaseUrl !== this.options.baseUrl) {
            throw protocolRefusal("inspected_save_authority_mismatch", "Inspected-save checkpoint belongs to a different Pigment host endpoint.", { checkpointBaseUrl: state.authorityBaseUrl, configuredBaseUrl: this.options.baseUrl });
        }
        return this.advanceInspectedSave(state, options);
    }
    async advanceInspectedSave(initial, options) {
        let state = initial;
        while (true) {
            if (state.phase === "committed")
                return state;
            if (state.phase === "inspection-failed")
                throw inspectedSaveFailure(state.failure);
            if (state.phase === "prepared") {
                const next = {
                    schema: PIGMENT_INSPECTED_SAVE_STATE_SCHEMA,
                    phase: "inspection-issued",
                    authorityBaseUrl: state.authorityBaseUrl,
                    prepared: state.prepared,
                    job: await this.requestInspectionJob(state.prepared),
                };
                await checkpointInspectedSave(next, options.checkpoint);
                state = next;
                continue;
            }
            if (state.phase === "inspection-issued") {
                const terminal = await this.awaitInspectionTerminal(state.prepared, state.job, {
                    ...(options.pollIntervalMs === undefined ? {} : { pollIntervalMs: options.pollIntervalMs }),
                    ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
                    ...(options.signal === undefined ? {} : { signal: options.signal }),
                });
                if (terminal.status === "failed") {
                    const report = await this.readInspectionReport(state.prepared, state.job, terminal.failure);
                    const next = {
                        schema: PIGMENT_INSPECTED_SAVE_STATE_SCHEMA,
                        phase: "inspection-failed",
                        authorityBaseUrl: state.authorityBaseUrl,
                        prepared: state.prepared,
                        job: state.job,
                        failure: terminal.failure,
                        report: report,
                    };
                    await checkpointInspectedSave(next, options.checkpoint);
                    throw inspectedSaveFailure(terminal.failure);
                }
                const report = await this.readInspectionReport(state.prepared, state.job, terminal.acceptance);
                const next = {
                    schema: PIGMENT_INSPECTED_SAVE_STATE_SCHEMA,
                    phase: "inspection-accepted",
                    authorityBaseUrl: state.authorityBaseUrl,
                    prepared: state.prepared,
                    job: state.job,
                    acceptance: terminal.acceptance,
                    report: report,
                };
                await checkpointInspectedSave(next, options.checkpoint);
                state = next;
                continue;
            }
            // Revalidate durable report/receipt bytes before mutation authority is
            // exercised. This catches a tampered checkpoint without trusting its phase.
            await assertPigmentInspectionReportRead(state.report, state.prepared.preview.artifact.id, state.job, state.acceptance);
            const committed = await this.commitPrepared(state.prepared, state.acceptance);
            const next = {
                schema: PIGMENT_INSPECTED_SAVE_STATE_SCHEMA,
                phase: "committed",
                authorityBaseUrl: state.authorityBaseUrl,
                prepared: state.prepared,
                job: state.job,
                acceptance: state.acceptance,
                report: state.report,
                committed,
            };
            await checkpointInspectedSave(next, options.checkpoint);
            return next;
        }
    }
    async request(path, options = {}) {
        const fetchFn = this.options.fetch || globalThis.fetch;
        if (!fetchFn)
            throw protocolRefusal("fetch_unavailable", "Pigment requires a fetch implementation.");
        const token = this.options.token === undefined ? null : await resolveToken(this.options.token);
        const timeoutMs = Math.max(100, Math.min(120_000, this.options.timeoutMs ?? 15_000));
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutMs);
        try {
            // Client-wide headers are useful for tracing and proxy metadata, but they
            // are not an authority surface. Build from those defaults first, then let
            // the exact operation contract own its capability and media-type fields.
            // Finally, a resolved token always owns Authorization. This prevents a
            // stale or malicious shared header bag from replacing the credential,
            // claim capability, or raw-PNG content type selected by this method.
            const headers = new Headers(this.options.headers);
            if (!headers.has("accept"))
                headers.set("accept", "application/json");
            for (const [name, value] of new Headers(options.headers))
                headers.set(name, value);
            if (options.body !== undefined)
                headers.set("content-type", "application/json");
            if (token)
                headers.set("authorization", `Bearer ${token}`);
            const response = await fetchFn(new URL(`/${path}`, this.options.baseUrl), {
                method: options.method || "GET",
                headers,
                ...(options.rawBody !== undefined ? { body: options.rawBody } : options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
                credentials: this.options.credentials ?? "same-origin",
                signal: controller.signal,
            });
            const body = await readJson(response);
            if (!response.ok)
                throw new PigmentRefusal(response.status, errorBody(body, "request_refused"));
            if (!isRecord(body))
                throw protocolRefusal("invalid_response", "Pigment host returned a non-object JSON response.", { path });
            return body;
        }
        catch (error) {
            if (error instanceof PigmentRefusal)
                throw error;
            if (error instanceof Error && error.name === "AbortError") {
                throw protocolRefusal("request_timeout", `Pigment request exceeded ${timeoutMs}ms.`, { path });
            }
            throw protocolRefusal("transport_failed", error instanceof Error ? error.message : String(error), { path });
        }
        finally {
            clearTimeout(timeout);
        }
    }
}
/**
 * Privileged browser-executor surface. Hosts must require a distinct executor
 * scope for every route below; possessing ordinary author authority is not
 * sufficient to upload evidence or submit a report.
 */
export class PigmentInspectionExecutorClient extends PigmentClient {
    constructor(options) {
        super(options);
    }
    /** Claim the oldest pending host job. The host binds the lease to this executor credential. */
    async claimInspectionJob(options = {}) {
        const request = assertPigmentInspectionExecutorClaimRequest({
            schema: "pigment.inspection-executor-claim-request.v2",
            requestId: options.requestId ?? inspectionClaimRequestId(),
        });
        const value = await this.request("api/site/inspection-jobs/claim", {
            method: "POST",
            body: request,
        });
        return assertPigmentInspectionExecutorClaim(value);
    }
    async uploadInspectionPng(claim, evidence, pngBytes) {
        const { job } = claim;
        const { screenshot } = evidence;
        assertPigmentInspectionExecutorClaim(claim);
        assertPigmentBrowserInspectionJob(job);
        assertPngBytes(pngBytes);
        const digest = await sha256PigmentBytes(pngBytes);
        if (screenshot.mediaType !== "image/png" || screenshot.sha256 !== digest) {
            throw protocolRefusal("inspection_blob_mismatch", "PNG bytes do not match the screenshot evidence digest.");
        }
        const value = await this.request(`api/site/inspection-jobs/${pathSegment(job.jobId, "jobId")}/blobs/${digest.slice("sha256:".length)}`, {
            method: "PUT",
            rawBody: exactArrayBuffer(pngBytes),
            headers: {
                "content-type": "image/png",
                "x-pigment-claim-generation": String(claim.claim.generation),
                "x-pigment-claim-token": claim.claim.token,
                "x-pigment-viewport-name": evidence.viewport.name,
                "x-pigment-pixel-width": String(screenshot.pixelWidth),
                "x-pigment-pixel-height": String(screenshot.pixelHeight),
            },
        });
        const descriptor = assertPigmentInspectionBlobDescriptor(value);
        if (descriptor.jobId !== job.jobId || descriptor.viewportName !== evidence.viewport.name || descriptor.sha256 !== digest
            || descriptor.pixelWidth !== screenshot.pixelWidth || descriptor.pixelHeight !== screenshot.pixelHeight) {
            throw protocolRefusal("inspection_blob_response_mismatch", "Host blob descriptor does not bind the uploaded PNG and dimensions.");
        }
        return descriptor;
    }
    async submitInspectionReport(claim, report) {
        const { job } = claim;
        assertPigmentInspectionExecutorClaim(claim);
        assertPigmentBrowserInspectionJob(job);
        assertPigmentBrowserInspectionReport(report, job);
        const value = await this.request(`api/site/inspection-jobs/${pathSegment(job.jobId, "jobId")}/reports`, {
            method: "POST",
            headers: {
                "x-pigment-claim-generation": String(claim.claim.generation),
                "x-pigment-claim-token": claim.claim.token,
            },
            body: { schema: "pigment.inspection-executor-submission.v1", jobId: job.jobId, report },
        });
        return await assertPigmentInspectionReportSubmissionResult(value, job, report);
    }
    async releaseInspectionJob(claim) {
        assertPigmentInspectionExecutorClaim(claim);
        const value = await this.request(`api/site/inspection-jobs/${pathSegment(claim.job.jobId, "jobId")}/release`, {
            method: "POST",
            headers: {
                "x-pigment-claim-generation": String(claim.claim.generation),
                "x-pigment-claim-token": claim.claim.token,
            },
            body: { schema: "pigment.inspection-executor-release.v1", jobId: claim.job.jobId },
        });
        if (!exactRecord(value, ["type", "ok", "status", "jobId"])
            || value.type !== "ok.pigment.inspection-executor-release.v1" || value.ok !== true
            || value.status !== "released" || value.jobId !== claim.job.jobId) {
            throw protocolRefusal("invalid_inspection_release", "Host returned an invalid inspection release result.");
        }
        return value;
    }
}
export function isPigmentInspectionExecutorRelease(value) {
    return exactRecord(value, ["schema", "jobId"])
        && value.schema === "pigment.inspection-executor-release.v1" && nonEmpty(value.jobId);
}
function assertPrepared(value) {
    if (!isRecord(value) || !exactRecord(value, ["schema", "page", "mutation", "manifestVersion", "observedDraftRevision", "plan", "preview", "preparedAt"])
        || value.schema !== "pigment.prepared-mutation.v1" || !nonEmpty(value.page) || !isRecord(value.mutation)
        || !nonEmpty(value.manifestVersion) || !nonNegativeInteger(value.observedDraftRevision)
        || !isRecord(value.plan) || !isRecord(value.preview) || typeof value.preparedAt !== "string" || !Number.isFinite(Date.parse(value.preparedAt))) {
        throw protocolRefusal("invalid_prepared_state", "Expected a pigment.prepared-mutation.v1 state file.");
    }
}
async function verifyPreparedMutationState(value) {
    assertPrepared(value);
    const mutation = value.mutation;
    const plan = value.plan;
    const preview = value.preview;
    const artifact = preview.artifact;
    if (!nonNegativeInteger(mutation.expectedRevision) || !Array.isArray(mutation.operations)
        || plan.type !== "ok.pigment.plan.v1" || plan.ok !== true || plan.page !== value.page
        || !nonNegativeInteger(plan.baseRevision) || !nonNegativeInteger(plan.candidateRevision) || !sha(plan.candidateHash)
        || !Array.isArray(plan.diff) || !isRecord(plan.validation) || !isRecord(plan.candidate)
        || preview.type !== "ok.pigment.preview.v1" || preview.ok !== true || !nonEmpty(preview.previewUrl)
        || !nonEmpty(preview.inspectionUrl) || !isRecord(artifact)
        || artifact.schema !== "ok.pigment.preview-artifact.v1" || !nonEmpty(artifact.id) || artifact.page !== value.page
        || !nonNegativeInteger(artifact.baseRevision) || !nonNegativeInteger(artifact.candidateRevision)
        || !sha(artifact.candidateHash) || !sha(artifact.mutationHash) || !sha(artifact.artifactHash)) {
        throw protocolRefusal("invalid_prepared_state", "Prepared Pigment state has an invalid plan or preview envelope.");
    }
    const expectedMutationHash = await sha256CanonicalPigmentJson({
        page: value.page,
        manifestVersion: value.manifestVersion,
        mutation: value.mutation,
    });
    if (value.observedDraftRevision !== mutation.expectedRevision
        || plan.baseRevision !== mutation.expectedRevision
        || artifact.baseRevision !== plan.baseRevision
        || artifact.candidateRevision !== plan.candidateRevision
        || artifact.candidateHash !== plan.candidateHash
        || artifact.mutationHash !== expectedMutationHash) {
        throw protocolRefusal("prepared_lineage_mismatch", "Persisted Pigment plan and preview do not retain one exact authority lineage.");
    }
}
function assertCommitted(value) {
    if (!isRecord(value) || value.schema !== "pigment.committed-mutation.v3" || !isRecord(value.prepared) || !isRecord(value.acceptance) || !isRecord(value.receipt)) {
        throw protocolRefusal("invalid_committed_state", "Expected a pigment.committed-mutation.v3 state file.");
    }
    assertPigmentPublishReceipt(value.receipt);
}
/** Validate a persisted inspected-save checkpoint before any resumed mutation. */
export async function assertPigmentInspectedSaveState(value) {
    if (!isRecord(value) || value.schema !== PIGMENT_INSPECTED_SAVE_STATE_SCHEMA
        || !["prepared", "inspection-issued", "inspection-accepted", "inspection-failed", "committed"].includes(String(value.phase))) {
        throw protocolRefusal("invalid_inspected_save_state", `Expected a ${PIGMENT_INSPECTED_SAVE_STATE_SCHEMA} checkpoint.`);
    }
    const expectedKeys = {
        prepared: ["schema", "phase", "authorityBaseUrl", "prepared"],
        "inspection-issued": ["schema", "phase", "authorityBaseUrl", "prepared", "job"],
        "inspection-accepted": ["schema", "phase", "authorityBaseUrl", "prepared", "job", "acceptance", "report"],
        "inspection-failed": ["schema", "phase", "authorityBaseUrl", "prepared", "job", "failure", "report"],
        committed: ["schema", "phase", "authorityBaseUrl", "prepared", "job", "acceptance", "report", "committed"],
    };
    if (!exactRecord(value, expectedKeys[String(value.phase)])) {
        throw protocolRefusal("invalid_inspected_save_state", "Inspected-save checkpoint contains missing or unexpected fields.");
    }
    assertCanonicalInspectedSaveBaseUrl(value.authorityBaseUrl);
    const prepared = value.prepared;
    await verifyPreparedMutationState(prepared);
    const binding = await createPigmentInspectionAuthorityBinding(prepared);
    if (value.phase === "prepared")
        return value;
    const job = value.job;
    assertPigmentBrowserInspectionJob(job);
    if (!sameJson(job.lineage, binding.preview)) {
        throw protocolRefusal("inspected_save_lineage_mismatch", "Checkpoint inspection job does not bind its prepared preview.");
    }
    if (value.phase === "inspection-issued")
        return value;
    if (value.phase === "inspection-failed") {
        if (!isPigmentInspectionFailure(value.failure)) {
            throw protocolRefusal("invalid_inspected_save_state", "Failed inspected-save checkpoint has no exact terminal failure.");
        }
        await assertPigmentInspectionReportRead(value.report, prepared.preview.artifact.id, job, value.failure);
        return value;
    }
    const acceptance = await verifyPigmentInspectionAcceptanceReceipt(value.acceptance, binding, { allowExpired: true });
    if (acceptance.body.inspection.jobId !== job.jobId) {
        throw protocolRefusal("inspected_save_lineage_mismatch", "Checkpoint acceptance does not bind its inspection job.");
    }
    await assertPigmentInspectionReportRead(value.report, prepared.preview.artifact.id, job, acceptance);
    if (value.phase === "inspection-accepted")
        return value;
    const committed = value.committed;
    await verifyCommittedMutation(committed);
    if (!sameJson(committed.prepared, prepared) || !sameJson(committed.acceptance, acceptance)) {
        throw protocolRefusal("inspected_save_lineage_mismatch", "Committed checkpoint does not retain its exact prepared state and acceptance.");
    }
    return value;
}
function assertCanonicalInspectedSaveBaseUrl(value) {
    if (typeof value !== "string" || !value) {
        throw protocolRefusal("invalid_inspected_save_state", "Inspected-save checkpoint has no canonical authority endpoint.");
    }
    try {
        const parsed = new URL(value);
        if ((parsed.protocol !== "https:" && parsed.protocol !== "http:")
            || parsed.username || parsed.password || parsed.origin !== value) {
            throw new Error("non-canonical endpoint");
        }
    }
    catch {
        throw protocolRefusal("invalid_inspected_save_state", "Inspected-save checkpoint has no canonical authority endpoint.");
    }
}
async function checkpointInspectedSave(state, checkpoint) {
    if (!checkpoint)
        return;
    // Detach the callback's value so it cannot mutate the state that authorizes
    // the next phase in the same process.
    await checkpoint(JSON.parse(canonicalPigmentJson(state)));
}
function inspectedSaveFailure(failure) {
    return new PigmentRefusal(422, {
        type: "pigment.inspection-failed.v1",
        error: failure.code,
        message: failure.message,
        failure,
    });
}
async function verifyCommittedMutation(value) {
    assertCommitted(value);
    await verifyPreparedMutationState(value.prepared);
    const binding = await createPigmentInspectionAuthorityBinding(value.prepared);
    const acceptance = await verifyPigmentInspectionAcceptanceReceipt(value.acceptance, binding, { allowExpired: true });
    const expectedReference = pigmentInspectionAcceptanceReference(acceptance);
    if (value.commit.previousRevision !== value.prepared.plan.baseRevision
        || value.commit.revision !== value.prepared.plan.candidateRevision
        || value.receipt.expectedDraftRevision !== value.commit.revision
        || value.receipt.expectedCandidateHash !== value.prepared.plan.candidateHash
        || !sameJson(value.receipt.inspectionAcceptance, expectedReference)) {
        throw protocolRefusal("committed_lineage_mismatch", "Committed Pigment state does not retain its exact plan, revision, candidate, and acceptance lineage.");
    }
}
export async function createPigmentInspectionAuthorityBinding(prepared) {
    assertPrepared(prepared);
    const artifact = prepared.preview.artifact;
    const mutationHash = await sha256CanonicalPigmentJson({
        page: prepared.page,
        manifestVersion: prepared.manifestVersion,
        mutation: prepared.mutation,
    });
    if (artifact.mutationHash !== mutationHash) {
        throw protocolRefusal("prepared_mutation_hash_mismatch", "Prepared mutation does not match the immutable preview mutation hash.");
    }
    return {
        schema: "pigment.inspection-authority-binding.v1",
        page: prepared.page,
        manifestVersion: prepared.manifestVersion,
        mutationHash,
        planHash: await sha256CanonicalPigmentJson(prepared.plan),
        preview: {
            previewId: artifact.id,
            parentPreviewId: artifact.parentPreviewId,
            page: artifact.page,
            baseRevision: artifact.baseRevision,
            candidateRevision: artifact.candidateRevision,
            candidateHash: artifact.candidateHash,
            mutationHash,
            artifactHash: artifact.artifactHash,
        },
    };
}
export async function verifyPigmentInspectionAcceptanceReceipt(value, expectedBinding, options = {}) {
    if (!isExactPigmentInspectionAcceptanceReceipt(value)) {
        throw protocolRefusal("invalid_inspection_acceptance", "Host returned an invalid Pigment inspection acceptance receipt.");
    }
    const body = value.body;
    const expectedHash = await sha256CanonicalPigmentJson(body);
    if (value.receiptHash !== expectedHash || (expectedBinding && !sameJson(body.binding, expectedBinding))) {
        throw protocolRefusal("inspection_acceptance_mismatch", "Inspection acceptance does not bind the exact prepared authority state.");
    }
    // Expiry gates new commit/publish authority. Hosts may still need to verify
    // the immutable receipt later when presenting durable audit evidence, so
    // that archival use can opt out of only the wall-clock freshness check.
    // Timestamp validity, ordering, and a non-future acceptance remain mandatory
    // in both modes. In particular, compare only after checking finiteness:
    // Date.parse() returns NaN for malformed dates, and comparisons against NaN
    // are false (which would otherwise fail open).
    const acceptedAt = Date.parse(body.acceptedAt);
    const expiresAt = Date.parse(body.expiresAt);
    const now = options.currentTimeMs ?? Date.now();
    const maxFutureSkewMs = options.maxFutureSkewMs ?? 30_000;
    if (!Number.isSafeInteger(now) || now < 0) {
        throw protocolRefusal("invalid_inspection_acceptance", "Inspection acceptance verification time must be a non-negative safe integer.");
    }
    if (!Number.isSafeInteger(maxFutureSkewMs) || maxFutureSkewMs < 0 || maxFutureSkewMs > 5 * 60_000) {
        throw protocolRefusal("invalid_inspection_acceptance", "Inspection acceptance clock-skew tolerance must be between 0 and 300000 milliseconds.");
    }
    if (!Number.isFinite(acceptedAt) || !Number.isFinite(expiresAt)
        || acceptedAt >= expiresAt || acceptedAt > now + maxFutureSkewMs
        || (!options.allowExpired && expiresAt <= now)) {
        throw protocolRefusal("inspection_acceptance_expired", "Inspection acceptance is expired or has invalid host timestamps.");
    }
    return value;
}
export function pigmentInspectionAcceptanceReference(receipt) {
    return { schema: "pigment.inspection-acceptance-reference.v1", receiptId: receipt.body.receiptId, receiptHash: receipt.receiptHash };
}
export function isPigmentInspectionAcceptanceReference(value) {
    return exactRecord(value, ["schema", "receiptId", "receiptHash"])
        && value.schema === "pigment.inspection-acceptance-reference.v1"
        && nonEmpty(value.receiptId) && sha(value.receiptHash);
}
export function assertPigmentInspectionAcceptanceReference(value) {
    if (!isPigmentInspectionAcceptanceReference(value))
        throw protocolRefusal("invalid_inspection_acceptance_reference", "Expected an exact pigment.inspection-acceptance-reference.v1 value.");
    return value;
}
export function isPigmentPublishReceipt(value) {
    return exactRecord(value, ["schema", "expectedDraftRevision", "expectedCandidateHash", "inspectionAcceptance"])
        && value.schema === "pigment.publish-receipt.v3"
        && Number.isSafeInteger(value.expectedDraftRevision) && Number(value.expectedDraftRevision) >= 0
        && sha(value.expectedCandidateHash)
        && isPigmentInspectionAcceptanceReference(value.inspectionAcceptance);
}
export function assertPigmentPublishReceipt(value) {
    if (!isPigmentPublishReceipt(value))
        throw protocolRefusal("invalid_publish_receipt", "Expected an exact pigment.publish-receipt.v3 value.");
    return value;
}
export function isPigmentPublishedDocumentReference(value) {
    return exactRecord(value, ["schema", "publicationId", "documentSha256", "publishReceiptSha256", "previousPublicationId", "publishedAt", "publishedBy"])
        && value.schema === "pigment.published-document-reference.v1"
        && typeof value.publicationId === "string" && /^ppub_[a-f0-9]{64}$/u.test(value.publicationId)
        && sha(value.documentSha256)
        && sha(value.publishReceiptSha256)
        && (value.previousPublicationId === null
            || (typeof value.previousPublicationId === "string" && /^ppub_[a-f0-9]{64}$/u.test(value.previousPublicationId)))
        && canonicalTimestamp(value.publishedAt)
        && exactRecord(value.publishedBy, ["identityId", "kind"])
        && nonEmpty(value.publishedBy.identityId)
        && value.publishedBy.identityId === value.publishedBy.identityId.trim()
        && ["human", "agent", "service"].includes(String(value.publishedBy.kind));
}
export function isPigmentPublished(value) {
    return exactRecord(value, ["type", "page", "published", "publication"])
        && value.type === "ok.site-content.published.v2"
        && nonEmpty(value.page) && value.page === value.page.trim()
        && isRecord(value.published)
        && isPigmentPublishedDocumentReference(value.publication);
}
export function isPigmentInspectionJobRequest(value) {
    return exactRecord(value, ["schema"]) && value.schema === "pigment.inspection-job-request.v1";
}
export function assertPigmentInspectionJobRequest(value) {
    if (!isPigmentInspectionJobRequest(value))
        throw protocolRefusal("invalid_inspection_job_request", "Expected an exact pigment.inspection-job-request.v1 value.");
    return value;
}
export function isPigmentInspectionExecutorClaimRequest(value) {
    return (exactRecord(value, ["schema"])
        && value.schema === "pigment.inspection-executor-claim-request.v1")
        || isPigmentInspectionExecutorClaimRequestV2(value);
}
export function isPigmentInspectionExecutorClaimRequestV2(value) {
    return exactRecord(value, ["schema", "requestId"])
        && value.schema === "pigment.inspection-executor-claim-request.v2"
        && typeof value.requestId === "string"
        && /^[A-Za-z0-9][A-Za-z0-9_-]{15,127}$/u.test(value.requestId);
}
export function assertPigmentInspectionExecutorClaimRequest(value) {
    if (!isPigmentInspectionExecutorClaimRequest(value))
        throw protocolRefusal("invalid_inspection_executor_claim_request", "Expected an exact Pigment executor claim request.");
    return value;
}
export function isPigmentInspectionExecutorClaim(value) {
    if (!isRecord(value) || value.type !== "ok.pigment.inspection-executor-claim.v1" || value.ok !== true)
        return false;
    if (value.status === "empty")
        return exactRecord(value, ["type", "ok", "status"]);
    if (value.status !== "claimed" || !exactRecord(value, ["type", "ok", "status", "job", "claim"]) || !isRecord(value.claim)
        || !exactRecord(value.claim, ["schema", "generation", "token", "expiresAt"])
        || value.claim.schema !== "pigment.inspection-claim-capability.v1"
        || !positiveInteger(value.claim.generation) || !nonEmpty(value.claim.token)
        || typeof value.claim.expiresAt !== "string" || !Number.isFinite(Date.parse(value.claim.expiresAt)))
        return false;
    try {
        assertPigmentBrowserInspectionJob(value.job);
        return true;
    }
    catch {
        return false;
    }
}
export function assertPigmentInspectionExecutorClaim(value) {
    if (!isPigmentInspectionExecutorClaim(value))
        throw protocolRefusal("invalid_inspection_executor_claim", "Host returned an invalid Pigment executor claim.");
    return value;
}
export function isPigmentInspectionExecutorSubmission(value) {
    if (!exactRecord(value, ["schema", "jobId", "report"]) || value.schema !== "pigment.inspection-executor-submission.v1" || !nonEmpty(value.jobId))
        return false;
    try {
        assertPigmentBrowserInspectionReport(value.report);
        return value.report.job.jobId === value.jobId;
    }
    catch {
        return false;
    }
}
export function assertPigmentInspectionExecutorSubmission(value) {
    if (!isPigmentInspectionExecutorSubmission(value))
        throw protocolRefusal("invalid_inspection_executor_submission", "Expected an exact report bound to pigment.inspection-executor-submission.v1.");
    return value;
}
export function isPigmentInspectionBlobDescriptor(value) {
    if (!exactRecord(value, ["schema", "jobId", "viewportName", "uri", "mediaType", "sha256", "bytes", "pixelWidth", "pixelHeight"])
        || value.schema !== "pigment.inspection-blob.v1" || !nonEmpty(value.jobId) || !nonEmpty(value.viewportName) || value.mediaType !== "image/png"
        || !sha(value.sha256) || !positiveInteger(value.bytes) || !positiveInteger(value.pixelWidth) || !positiveInteger(value.pixelHeight))
        return false;
    try {
        const url = new URL(String(value.uri));
        return url.protocol === "https:" || url.protocol === "http:";
    }
    catch {
        return false;
    }
}
export function assertPigmentInspectionBlobDescriptor(value) {
    if (!isPigmentInspectionBlobDescriptor(value))
        throw protocolRefusal("invalid_inspection_blob_descriptor", "Host must return an exact blob descriptor with an absolute retrievable HTTP(S) URI.");
    return value;
}
export function withPigmentInspectionBlob(report, viewportName, blob) {
    assertPigmentBrowserInspectionReport(report);
    if (blob.jobId !== report.job.jobId)
        throw protocolRefusal("inspection_blob_job_mismatch", "Uploaded PNG belongs to a different inspection job.");
    if (blob.viewportName !== viewportName)
        throw protocolRefusal("inspection_blob_viewport_mismatch", "Uploaded PNG belongs to a different inspection viewport.");
    const evidence = report.evidence.map((item) => item.viewport.name === viewportName
        ? { ...item, screenshot: { uri: blob.uri, mediaType: blob.mediaType, sha256: blob.sha256, pixelWidth: blob.pixelWidth, pixelHeight: blob.pixelHeight } }
        : item);
    if (!report.evidence.some((item) => item.viewport.name === viewportName))
        throw protocolRefusal("inspection_viewport_missing", `Report has no ${viewportName} viewport.`);
    const bound = { ...report, evidence };
    assertPigmentBrowserInspectionReport(bound, report.job);
    return bound;
}
export function assertPigmentInspectionJobResponse(value) {
    if (!exactRecord(value, ["type", "ok", "job"]) || value.type !== "ok.pigment.inspection-job.v1" || value.ok !== true) {
        throw protocolRefusal("invalid_inspection_job_response", "Pigment host returned an invalid inspection-job response.");
    }
    assertPigmentBrowserInspectionJob(value.job);
    return value;
}
export async function assertPigmentInspectionAcceptanceStatus(value, expectedBinding) {
    if (!isRecord(value) || value.type !== "ok.pigment.inspection-acceptance-status.v1" || value.ok !== true
        || !nonEmpty(value.jobId) || !["pending", "accepted", "failed"].includes(String(value.status))) {
        throw protocolRefusal("invalid_inspection_acceptance_status", "Host returned an invalid inspection-acceptance status.");
    }
    if (value.status === "pending") {
        if (!exactRecord(value, ["type", "ok", "status", "jobId"]))
            throw protocolRefusal("invalid_inspection_acceptance_status", "Pending acceptance status contains unexpected authority data.");
        return value;
    }
    if (value.status === "failed") {
        if (!exactRecord(value, ["type", "ok", "status", "jobId", "failure"])
            || !isPigmentInspectionFailure(value.failure))
            throw protocolRefusal("invalid_inspection_acceptance_status", "Failed status must carry one exact host failure.");
        return value;
    }
    if (!exactRecord(value, ["type", "ok", "status", "jobId", "acceptance"]))
        throw protocolRefusal("invalid_inspection_acceptance_status", "Accepted status must carry exactly one host receipt.");
    const acceptance = await verifyPigmentInspectionAcceptanceReceipt(value.acceptance, expectedBinding, { allowExpired: true });
    if (acceptance.body.inspection.jobId !== value.jobId) {
        throw protocolRefusal("inspection_acceptance_mismatch", "Accepted status receipt does not bind the envelope inspection job.");
    }
    return { type: "ok.pigment.inspection-acceptance-status.v1", ok: true, status: "accepted", jobId: value.jobId, acceptance };
}
async function assertPigmentInspectionReportRead(value, previewId, job, terminal) {
    if (!isRecord(value) || value.type !== "ok.pigment.inspection-report.v1" || value.ok !== true
        || value.previewId !== previewId || value.jobId !== job.jobId || !sha(value.reportHash)) {
        throw protocolRefusal("invalid_inspection_report_read", "Host returned an invalid author-readable inspection report envelope.");
    }
    const failed = isPigmentInspectionFailure(terminal);
    if (!exactRecord(value, failed
        ? ["type", "ok", "previewId", "jobId", "report", "reportHash", "failure"]
        : ["type", "ok", "previewId", "jobId", "report", "reportHash"])) {
        throw protocolRefusal("invalid_inspection_report_read", "Inspection report envelope contains unexpected terminal authority data.");
    }
    if (failed) {
        if (!isPigmentInspectionFailure(value.failure) || !sameJson(value.failure, terminal)) {
            throw protocolRefusal("inspection_failure_mismatch", "Inspection report does not carry the exact terminal host failure.");
        }
        if (terminal.code === "inspection_executor_attempts_exhausted") {
            if (!isPigmentInspectionExecutorExhaustionReport(value.report)
                || value.report.jobId !== job.jobId || value.report.reportId !== terminal.reportId) {
                throw protocolRefusal("inspection_exhaustion_report_mismatch", "Executor exhaustion report does not bind the requested inspection job.");
            }
            const reportHash = await sha256CanonicalPigmentJson(value.report);
            if (value.reportHash !== reportHash || terminal.reportHash !== reportHash) {
                throw protocolRefusal("inspection_report_hash_mismatch", "Executor exhaustion report bytes do not match the host envelope and terminal failure.");
            }
            return value;
        }
        if (terminal.code !== "inspection_evidence_adverse") {
            throw protocolRefusal("inspection_failure_kind_unsupported", "Terminal inspection failure does not name a supported durable evidence kind.");
        }
        try {
            assertPigmentBrowserInspectionReport(value.report);
        }
        catch (error) {
            throw protocolRefusal("invalid_inspection_report", error instanceof Error ? error.message : "Host returned an invalid adverse browser inspection report.");
        }
        if (!sameJson(value.report.job, job)) {
            throw protocolRefusal("inspection_report_job_mismatch", "Inspection report does not bind the requested browser inspection job.");
        }
        if (value.report.reportId !== terminal.reportId || value.report.outcome !== "failed") {
            throw protocolRefusal("inspection_report_outcome_mismatch", "Adverse browser evidence must be the exact failed report named by the terminal failure.");
        }
        const reportHash = await sha256CanonicalPigmentJson(value.report);
        if (value.reportHash !== reportHash || terminal.reportHash !== reportHash) {
            throw protocolRefusal("inspection_report_hash_mismatch", "Adverse browser report bytes do not match the host envelope and terminal failure.");
        }
        return value;
    }
    try {
        assertPigmentBrowserInspectionReport(value.report);
    }
    catch (error) {
        throw protocolRefusal("invalid_inspection_report", error instanceof Error ? error.message : "Host returned an invalid browser inspection report.");
    }
    if (!sameJson(value.report.job, job)) {
        throw protocolRefusal("inspection_report_job_mismatch", "Inspection report does not bind the requested browser inspection job.");
    }
    if (value.report.reportId !== terminal.body.inspection.reportId) {
        throw protocolRefusal("inspection_report_id_mismatch", "Inspection report does not match the accepted report identifier.");
    }
    if (value.report.outcome !== "passed") {
        throw protocolRefusal("inspection_report_outcome_mismatch", "Accepted browser evidence must contain a passed inspection report.");
    }
    const reportHash = await sha256CanonicalPigmentJson(value.report);
    if (value.reportHash !== reportHash || terminal.body.inspection.reportHash !== reportHash) {
        throw protocolRefusal("inspection_report_hash_mismatch", "Inspection report bytes do not match the host envelope and accepted receipt.");
    }
    return value;
}
function isPigmentInspectionExecutorExhaustionReport(value) {
    return exactRecord(value, ["schema", "reportId", "jobId", "attempts", "lastClaimExpiredAt"])
        && value.schema === "pigment.inspection-executor-exhaustion.v1"
        && typeof value.reportId === "string" && /^pir_executor_[a-f0-9]{32}$/u.test(value.reportId)
        && nonEmpty(value.jobId)
        && Number.isSafeInteger(value.attempts) && Number(value.attempts) >= 3
        && (value.lastClaimExpiredAt === null || (typeof value.lastClaimExpiredAt === "string" && Number.isFinite(Date.parse(value.lastClaimExpiredAt))));
}
async function assertPigmentInspectionReportSubmissionResult(value, job, report) {
    if (!isRecord(value)
        || value.type !== "ok.pigment.inspection-report-submitted.v1" || value.ok !== true
        || value.jobId !== job.jobId || value.reportId !== report.reportId) {
        throw protocolRefusal("invalid_inspection_report_submission", "Host returned an invalid report-submission result.");
    }
    if (value.status === "failed") {
        if (!exactRecord(value, ["type", "ok", "status", "jobId", "reportId", "failure"])
            || !isPigmentInspectionFailure(value.failure))
            throw protocolRefusal("invalid_inspection_report_submission", "Host returned an invalid failed inspection result.");
        return value;
    }
    if (value.status !== "accepted" || !exactRecord(value, ["type", "ok", "status", "jobId", "reportId", "acceptance"])) {
        throw protocolRefusal("invalid_inspection_report_submission", "Host returned an invalid accepted inspection result.");
    }
    const acceptance = await verifyPigmentInspectionAcceptanceReceipt(value.acceptance, undefined, { allowExpired: true });
    const reportHash = await sha256CanonicalPigmentJson(report);
    if (acceptance.body.inspection.jobId !== job.jobId
        || acceptance.body.inspection.reportId !== report.reportId
        || acceptance.body.inspection.reportHash !== reportHash
        || !sameJson(acceptance.body.binding.preview, job.lineage)) {
        throw protocolRefusal("inspection_acceptance_mismatch", "Host acceptance does not bind the exact submitted job, report bytes, and preview lineage.");
    }
    return { type: "ok.pigment.inspection-report-submitted.v1", ok: true, status: "accepted", jobId: job.jobId, reportId: report.reportId, acceptance };
}
export function isPigmentInspectionFailure(value) {
    return exactRecord(value, ["schema", "code", "message", "reportId", "reportHash", "failedAt"])
        && value.schema === "pigment.inspection-failure.v1" && nonEmpty(value.code) && nonEmpty(value.message)
        && nonEmpty(value.reportId) && sha(value.reportHash) && typeof value.failedAt === "string"
        && Number.isFinite(Date.parse(value.failedAt));
}
function exactRecord(value, keys) {
    return isRecord(value) && Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}
function canonicalTimestamp(value) {
    if (typeof value !== "string")
        return false;
    const milliseconds = Date.parse(value);
    return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}
function exactPigmentPreviewLineage(value) {
    return exactRecord(value, ["previewId", "parentPreviewId", "page", "baseRevision", "candidateRevision", "candidateHash", "mutationHash", "artifactHash"])
        && nonEmpty(value.previewId) && value.previewId === value.previewId.trim()
        && (value.parentPreviewId === null || (nonEmpty(value.parentPreviewId) && value.parentPreviewId === value.parentPreviewId.trim()))
        && nonEmpty(value.page) && value.page === value.page.trim()
        && Number.isSafeInteger(value.baseRevision) && Number(value.baseRevision) >= 0
        && Number.isSafeInteger(value.candidateRevision) && Number(value.candidateRevision) > Number(value.baseRevision)
        && sha(value.candidateHash) && sha(value.mutationHash) && sha(value.artifactHash);
}
function exactPigmentInspectionAuthorityBinding(value) {
    return exactRecord(value, ["schema", "page", "manifestVersion", "mutationHash", "planHash", "preview"])
        && value.schema === "pigment.inspection-authority-binding.v1"
        && nonEmpty(value.page) && value.page === value.page.trim()
        && nonEmpty(value.manifestVersion) && value.manifestVersion === value.manifestVersion.trim()
        && sha(value.mutationHash) && sha(value.planHash)
        && exactPigmentPreviewLineage(value.preview)
        && value.preview.page === value.page
        && value.preview.mutationHash === value.mutationHash;
}
function isExactPigmentInspectionAcceptanceReceipt(value) {
    if (!exactRecord(value, ["schema", "body", "receiptHash"])
        || value.schema !== "pigment.inspection-acceptance-receipt.v1"
        || !sha(value.receiptHash)
        || !exactRecord(value.body, ["schema", "receiptId", "audience", "actor", "binding", "inspection", "acceptedAt", "expiresAt"]))
        return false;
    const body = value.body;
    return body.schema === "pigment.inspection-acceptance-body.v1"
        && nonEmpty(body.receiptId) && body.receiptId === body.receiptId.trim()
        && nonEmpty(body.audience) && body.audience === body.audience.trim()
        && exactRecord(body.actor, ["identityId", "kind"])
        && nonEmpty(body.actor.identityId) && body.actor.identityId === body.actor.identityId.trim()
        && ["human", "agent", "service"].includes(String(body.actor.kind))
        && exactPigmentInspectionAuthorityBinding(body.binding)
        && exactRecord(body.inspection, ["jobId", "reportId", "reportHash", "outcome"])
        && nonEmpty(body.inspection.jobId) && body.inspection.jobId === body.inspection.jobId.trim()
        && nonEmpty(body.inspection.reportId) && body.inspection.reportId === body.inspection.reportId.trim()
        && sha(body.inspection.reportHash) && body.inspection.outcome === "passed"
        && canonicalTimestamp(body.acceptedAt) && canonicalTimestamp(body.expiresAt);
}
function nonEmpty(value) {
    return typeof value === "string" && value.trim().length > 0;
}
function positiveInteger(value) {
    return Number.isSafeInteger(value) && Number(value) > 0;
}
function nonNegativeInteger(value) {
    return Number.isSafeInteger(value) && Number(value) >= 0;
}
function assertPngBytes(bytes) {
    const signature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    if (bytes.length <= signature.length || !signature.every((value, index) => bytes[index] === value)) {
        throw protocolRefusal("invalid_inspection_png", "Inspection evidence must contain PNG bytes with a valid signature.");
    }
}
function exactArrayBuffer(bytes) {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}
function inspectionClaimRequestId() {
    if (!globalThis.crypto?.getRandomValues)
        throw protocolRefusal("crypto_unavailable", "Pigment executor requires Web Crypto randomness for claim idempotency.");
    const bytes = new Uint8Array(32);
    globalThis.crypto.getRandomValues(bytes);
    return `picr_${Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("")}`;
}
async function sha256PigmentBytes(bytes) {
    if (!globalThis.crypto?.subtle)
        throw protocolRefusal("crypto_unavailable", "Pigment executor requires Web Crypto SHA-256 support.");
    const digest = await globalThis.crypto.subtle.digest("SHA-256", exactArrayBuffer(bytes));
    return `sha256:${Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, "0")).join("")}`;
}
async function waitFor(milliseconds, signal) {
    await new Promise((resolve, reject) => {
        if (signal?.aborted)
            return reject(protocolRefusal("inspection_wait_aborted", "Waiting for browser inspection was aborted."));
        const onAbort = () => {
            clearTimeout(timer);
            reject(protocolRefusal("inspection_wait_aborted", "Waiting for browser inspection was aborted."));
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, milliseconds);
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}
function sha(value) {
    return typeof value === "string" && /^sha256:[a-f0-9]{64}$/u.test(value);
}
function sameJson(left, right) {
    return canonicalPigmentJson(left) === canonicalPigmentJson(right);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function errorBody(value, fallback) {
    return isRecord(value) ? value : { type: "pigment.client-error.v1", error: fallback, detail: String(value) };
}
function protocolRefusal(error, message, detail = {}) {
    return new PigmentRefusal(502, { type: "pigment.client-error.v1", error, message, ...detail });
}
async function resolveToken(provider) {
    const token = typeof provider === "function" ? await provider() : provider;
    if (!token.trim())
        throw protocolRefusal("credential_missing", "Pigment token provider returned an empty credential.");
    return token.trim();
}
function pathSegment(value, field) {
    const trimmed = value.trim();
    if (!trimmed)
        throw new Error(`Pigment ${field} must not be empty.`);
    return encodeURIComponent(trimmed);
}
function pageSegment(page) {
    return pathSegment(page, "page");
}
async function readJson(response) {
    const text = await response.text();
    if (!text)
        return {};
    try {
        return JSON.parse(text);
    }
    catch {
        throw protocolRefusal("invalid_json_response", `Pigment host returned non-JSON for HTTP ${response.status}.`);
    }
}
